import {
  CurrencyCodes,
  PayableEntityType,
  PaymentProviders,
  TransactionDirection,
} from "@shared_packages/toolkit/types";
import { type ColumnDef } from "@tanstack/table-core";
import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { CheckBlue, Close } from "../../../../../components/icons";
import {
  AmountCell,
  CopyIDInput,
  CreateEntityTableCell,
  DateTableCell,
  PaginationControl,
  ReactTableComponent,
  Status,
  Typography,
  TypographyVariant,
} from "../../../../../components/ui-kit";
import { AppRoutes } from "../../../../../constants/routes";
import { getFieldFromRawData } from "../../../../../util/data-utils";
import { PaymentApiType, TransactionApiType } from "../../../../../util/types";
import { TransactionsProviderTypes } from "../list/TransactionsList";

const StyledReactTableComponent = styled(ReactTableComponent)`
  td {
    height: 45px;
  }
`;

const RefundTypography = styled(Typography)`
  white-space: nowrap;
`;

const Cell = styled.div`
  display: flex;
  padding: 15px 10px;
`;

const CheckContainer = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TransactionsTable: FC<{
  data: TransactionApiType[];
  pagination: PaginationControl;
  type?: TransactionsProviderTypes;
  openInNewTab?: boolean;
}> = ({ data, pagination, type, openInNewTab = false }) => {
  const navigate = useNavigate();
  const columns = React.useMemo<Array<ColumnDef<TransactionApiType, any>>>(
    () => [
      {
        header: "Sandbox",
        accessorKey: "isSandbox",
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <Cell style={{ justifyContent: "center" }}>
            {original.isSandbox ? (
              <CheckContainer>
                <CheckBlue />
              </CheckContainer>
            ) : (
              <Close />
            )}
          </Cell>
        ),
      },
      {
        header: "Fena ID",
        accessorKey: "_id",
        enableSorting: false,
        cell: ({ getValue }) => (
          <Cell>
            <CopyIDInput
              uniqueTooltipId={getValue()}
              hideInput
              text={getValue()}
            />
          </Cell>
        ),
      },
      {
        header: "Reference",
        meta: { mobileTitle: true },
        accessorKey: "reference",
        cell: ({ row: { original } }) => (
          <Cell style={{ flexDirection: "column" }}>
            <Typography>{original.reference}</Typography>
            {original.direction === TransactionDirection.OUTGOING && (
              <RefundTypography
                variant={TypographyVariant.BODY6}
                color={"#EF6355"}
              >
                ● Refund
              </RefundTypography>
            )}
          </Cell>
        ),
      },
      {
        header: "Amount",
        accessorKey: "amount",
        cell: (info) => {
          return (
            <Cell>
              <AmountCell
                currency={
                  info.row.original.currency ?? CurrencyCodes.POUND_STERLING
                }
                amount={info.getValue()}
                direction={info.row.original.direction}
              />
            </Cell>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <Cell style={{ display: "block" }}>
              <Status status={original?.status} />
            </Cell>
          );
        },
      },
      {
        header: "Type",
        accessorKey: "parentEntity",
        enableSorting: false,
        cell: (info) => {
          if (
            info.row.original.parentEntityType === PayableEntityType.PAYMENT
          ) {
            const type = info.row.original.parentEntityPaymentProvider;
            if (info.row.original.parentEntityPaymentProvider) {
              switch (type) {
                case PaymentProviders.FENA_OB:
                  return <Cell>Link payment</Cell>;
                case PaymentProviders.FENA_OB_QR:
                  return <Cell>QR payment</Cell>;
                case PaymentProviders.CASH:
                  return <Cell>Cash payment</Cell>;
                case PaymentProviders.OTHER:
                  return <Cell>Other payment</Cell>;
                case PaymentProviders.FENA_MOBILE:
                  return <Cell>Mobile payment</Cell>;
                case PaymentProviders.FENA_STRIPE:
                  // Legacy provider value still tags historical rows,
                  // but admins shouldn't see "Stripe" in the UI now
                  // that we've moved off it.
                  return <Cell>Card payment</Cell>;
                case PaymentProviders.FENA_CHECKOUT:
                  return <Cell>Checkout payment</Cell>;
                case PaymentProviders.FENA_MOBILE_TOUCH_TO_PAY:
                  return <Cell>Touch to pay</Cell>;
                case PaymentProviders.CARD:
                  return <Cell>Card payment</Cell>;
                case PaymentProviders.NONE:
                  return <Cell>N/A</Cell>;
              }
            } else {
              return <Cell>Unknown</Cell>;
            }
          } else if (
            info.row.original.parentEntityType ===
            PayableEntityType.RECURRING_PAYMENT
          ) {
            return <Cell>Fena recurring payment</Cell>;
          } else if (
            info.row.original.parentEntityType === PayableEntityType.VRP
          ) {
            return <Cell>VRP payment</Cell>;
          }
        },
      },
      {
        header: "Remitter bank",
        accessorKey: "rawData",
        cell: ({ row: { original } }) => {
          return (
            <Cell>
              {getFieldFromRawData(
                "data.refund.provider.id",
                original.rawData
              ) ??
                getFieldFromRawData(
                  "data.payment.provider.id",
                  original.rawData
                ) ??
                "-"}
            </Cell>
          );
        },
      },

      {
        header: "Owner name",
        accessorKey: "ownerName",
        enableSorting: false,
        cell: (info) => {
          const ownerNameFromAccessor = info.getValue() as string | undefined;
          const original: any = info.row.original as any;
          const fallbackName =
            original?.owner?.tradingName ?? original?.owner?.name ?? undefined;
          const displayName = ownerNameFromAccessor ?? fallbackName ?? "-";
          return <Cell>{displayName}</Cell>;
        },
      },
      {
        header: "Completed",
        accessorKey: "completedAt",
        sortDescFirst: false,
        invertSorting: true,
        cell: (info) => (
          <Cell>
            <DateTableCell date={info.getValue()} />
          </Cell>
        ),
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        cell: (info) => (
          <Cell>
            <DateTableCell date={info.getValue()} />
          </Cell>
        ),
      },
      {
        header: "Created by",
        accessorKey: "createdBy",
        enableSorting: false,
        cell: (info) => {
          return (
            <Cell>
              <CreateEntityTableCell
                createdByEntity={info.row.original?.createdByEntity}
                createdBy={info.row.original?.createdBy}
              />
            </Cell>
          );
        },
      },
    ],
    []
  );

  const onDetailsClick = (transaction: TransactionApiType) => {
    const url =
      AppRoutes.Private.Payments.TRANSACTION_DETAILS + "/" + transaction._id;
    if (openInNewTab) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <StyledReactTableComponent
      onRowClick={onDetailsClick}
      columns={columns}
      data={data}
      withPagination={pagination}
      withSorting
      padding="0"
    />
  );
};
